import { useFieldArray, useFormContext } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../../lib/messages';
import { emptyCondition, emptySet, type RewriteFormValue } from '../../../rewriteForm';
import { RewriteConditionGroup } from '../RewriteConditionGroup';
import { RewriteSetRow } from '../RewriteSetRow';

export function RewriteRuleFields({
  ruleIndex,
  canRemove,
  onRemove,
}: {
  ruleIndex: number;
  canRemove: boolean;
  onRemove: () => void;
}) {
  const form = useFormContext<RewriteFormValue>();
  const ruleKey = ruleIndex.toString();
  const groups = useFieldArray({
    control: form.control,
    name: `rules.${ruleKey}.condGroups` as `rules.${number}.condGroups`,
  });
  const sets = useFieldArray({
    control: form.control,
    name: `rules.${ruleKey}.sets` as `rules.${number}.sets`,
  });
  return (
    <section className="border-input overflow-hidden rounded-md border">
      <header className="bg-muted/40 border-input flex items-center justify-between gap-3 border-b px-4 py-3">
        <h2 className="text-base font-semibold">
          {dashboardT('lists.rewrite.rule', { number: ruleIndex + 1 })}
        </h2>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-9 w-9"
          disabled={!canRemove}
          onClick={onRemove}
          aria-label={dashboardT('lists.rewrite.removeRule')}
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
        </Button>
      </header>
      <div className="space-y-4 px-4 py-4">
        <div className="space-y-3">
          {groups.fields.map((group, groupIndex) => (
            <RewriteConditionGroup
              key={group.id}
              ruleIndex={ruleIndex}
              groupIndex={groupIndex}
              onRemove={() => {
                groups.remove(groupIndex);
              }}
            />
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              groups.append({ conditions: [emptyCondition()] });
            }}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            {dashboardT('lists.rewrite.addOr')}
          </Button>
        </div>
        <div className="border-input space-y-3 border-t pt-4">
          <h3 className="text-sm font-semibold">{dashboardT('lists.rewrite.then')}</h3>
          {sets.fields.map((set, setIndex) => (
            <RewriteSetRow
              key={set.id}
              ruleIndex={ruleIndex}
              setIndex={setIndex}
              canRemove={sets.fields.length > 1}
              onRemove={() => {
                sets.remove(setIndex);
              }}
            />
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              sets.append(emptySet());
            }}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            {dashboardT('lists.rewrite.addSet')}
          </Button>
        </div>
      </div>
    </section>
  );
}
